<template>
  <div>
    <!-- ACTIVITY TRACKER -->
    <h4>Today's Activity</h4>
    <p class="small-text">Each bubble represents one video count from today. The bubble is placed at the time when the video was counted (<i>not</i> when the video was recorded). The size of the bubble indicates how many fish were counted in that video. Hover your mouse over the bubbles to see more information, or click and drag to zoom in.</p>

    <div class="ui grid padded stackable equal width middle aligned">
      <div class="ui four wide column centered">
        <div class="odometer-wrapper">
          <div class="odometer-value">
            <span id="odometer-activity-total" class="odometer">2,500</span>
          </div>
        </div>
        <div class="odometer-label">
          Videos Counted Today
        </div>
      </div>
      <div class="ui column">
        <div id="chart-activity"></div>
      </div>
    </div>

    <!-- ESTIMATED RUN -->
    <h4>Total Estimated Fish Run</h4>
    <p class="small-text">Each bar shows our current estimate of the total number of fish migrating each day. These totals are updated in real-time using a statistical model that estimates how many fish migrate <em>over the entire day</em> based the individual counts of videos recorded on that day. The error bars represent our uncertainty in the daily total (95% confidence interval).</p>

    <div class="ui grid padded stackable equal width middle aligned">
      <div class="ui four wide column centered">
        <div>
          <div class="odometer-wrapper">
            <div class="odometer-value">
              <span id="odometer-run-total" class="odometer">0</span>
            </div>
            <div>
              +/- <span id="odometer-run-range" class="odometer">0</span>
            </div>
          </div>
        </div>
        <div class="odometer-label">
          Total Estimated Fish Run
        </div>
      </div>
      <div class="ui column">
        <div id="chart-run"></div>
      </div>
    </div>

    <!-- FISH STATUS -->
    <h4>Number of Fish Counted</h4>
    <p class="small-text">Each bar shows the total number of fish that have been counted for each day (so far). These totals will increase as more videos are watched. Note that these totals tend to be much less than those in the chart above, which uses these counts to estimate the total number of fish passing over the entire day, not just during the videos that have been counted so far.</p>

    <div class="ui grid padded stackable equal width middle aligned">
      <div class="ui four wide column">
        <div>
          <div class="odometer-wrapper">
            <div class="odometer-value">
              <span id="odometer-fish-total" class="odometer">0</span>
            </div>
          </div>
        </div>
        <div class="odometer-label">
          Total Fish Counted
        </div>
      </div>
      <div class="ui column">
        <div id="chart-fish"></div>
      </div>
    </div>

    <!-- VIDEO STATUS -->
    <h4>Number of Videos</h4>
    <p class="small-text">Each bar shows how many videos were recorded each day, plus how many of those have been counted and how many remain to be counted. The number of recorded videos depends on how active the fish were on that day, but also whether the camera was properly configured to only record when it detected fish and not other things floating by.</p>

    <div class="ui grid padded stackable equal width middle aligned">
      <div class="ui four wide column">
        <div>
          <div class="odometer-wrapper">
            <div class="odometer-value">
              <span id="odometer-video-counted" class="odometer">0</span>
            </div>
          </div>
        </div>
        <div class="odometer-label">
          Total Videos Counted
        </div>
        <div style="margin-top:24px">
          <div class="odometer-wrapper">
            <div class="odometer-value">
              <span id="odometer-video-remaining" class="odometer">0</span>
            </div>
          </div>
        </div>
        <div class="odometer-label">
          Total Videos Remaining
        </div>
      </div>
      <div class="ui column">
        <div id="chart-video"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3Time from 'd3-time'
import * as d3TimeFormat from 'd3-time-format'
import Odometer from 'odometer'
import jStat from 'jstat'

import evt from '@/events'

export default {
  name: 'DataSummary',
  data () {
    return {
      locationId: process.env.VUE_APP_LOCATION_ID,
      runYear: process.env.VUE_APP_RUN_YEAR,
      data: {
        activity: {
          total: 0,
          values: []
        },
        run: {
          total: {
            y: 0,
            range: 0
          },
          values: []
        },
        fish: {
          total: 0,
          values: []
        },
        video: {
          total: {
            counted: 0,
            remaining: 0
          },
          values: []
        }
      },
      charts: {
        activity: null,
        run: null,
        fish: null,
        video: null
      },
      odometers: {
        activity: null,
        run: {
          total: null,
          range: null
        },
        fish: null,
        video: {
          counted: null,
          remaining: null
        }
      }
    }
  },
  mounted () {
    const colors = this.$highcharts.getOptions().colors
    let today = d3Time.timeDay.floor(new Date())
    if (today.getUTCHours() < 4) {
      today = new Date(today.getTime() - 86400 * 1000)
    }
    const tomorrow = new Date(today.getTime() + 86400 * 1000)

    this.odometers.activity = new Odometer({
      el: document.getElementById('odometer-activity-total'),
      value: 0
    })
    this.odometers.run.total = new Odometer({
      el: document.getElementById('odometer-run-total'),
      value: 0
    })
    this.odometers.run.range = new Odometer({
      el: document.getElementById('odometer-run-range'),
      value: 0
    })
    this.odometers.fish = new Odometer({
      el: document.getElementById('odometer-fish-total'),
      value: 0
    })
    this.odometers.video.counted = new Odometer({
      el: document.getElementById('odometer-video-counted'),
      value: 0
    })
    this.odometers.video.remaining = new Odometer({
      el: document.getElementById('odometer-video-remaining'),
      value: 0
    })

    this.charts.activity = this.$highcharts.chart('chart-activity', {
      chart: {
        type: 'bubble',
        height: 150,
        // marginLeft: 75,
        zoomType: 'x'
      },
      title: {
        text: ''
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          month: '%b %d',
          day: '%b %d'
        },
        title: {
          text: 'Time'
        },
        min: today.valueOf(),
        max: tomorrow.valueOf()
      },
      yAxis: {
        visible: false
      },
      plotOptions: {
        bubble: {
          marker: {
            fillOpacity: 0.1
          },
          minSize: 2,
          maxSize: 50
        }
      },
      legend: {
        enabled: false
      },
      tooltip: {
        useHTML: true,
        headerFormat: '<table>',
        pointFormat:
          '<tr><th style="text-align:right"># Fish Counted:</th><td style="padding-left:10px">{point.z}</td></tr>' +
          '<tr><th style="text-align:right">Counted At:</th><td style="padding-left:10px">{point.count_timestamp}</td></tr>' +
          '<tr><th style="text-align:right">Video Recorded At:</th><td style="padding-left:10px">{point.video_timestamp}</td></tr>' +
          '<tr><th style="text-align:right">Video Duration:</th><td style="padding-left:10px">{point.duration} sec</td></tr>',
        footerFormat: '</table>',
        followPointer: false,
        hideDelay: 250
      },
      series: [
        {
          name: 'Video Count',
          data: []
        }
      ]
    })
    this.charts.run = this.$highcharts.chart('chart-run', {
      chart: {
        type: 'column',
        height: 275,
        marginLeft: 75
      },
      title: {
        text: ''
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          month: '%b %d',
          week: '%b %d',
          day: '%b %d'
        },
        title: {
          text: 'Date'
        }
        // min: this.dateRange[0].valueOf(),
        // max: this.dateRange[1].valueOf()
      },
      yAxis: {
        title: {
          text: 'Estimated # Fish per Day'
        }
      },
      plotOptions: {
        column: {
          groupPadding: 0,
          pointPadding: 0
        }
      },
      legend: {
        enabled: false
      },
      series: [
        {
          name: 'Estimated # Fish',
          data: [],
          type: 'column',
          tooltip: {
            valueDecimals: 0
          }
        },
        {
          name: 'Uncertainty Range',
          data: [],
          type: 'errorbar',
          tooltip: {
            valueDecimals: 0
          }
        }
      ],
      tooltip: {
        shared: true
      }
    })
    this.charts.fish = this.$highcharts.chart('chart-fish', {
      chart: {
        type: 'column',
        height: 275,
        marginLeft: 75
      },
      title: {
        text: ''
      },
      plotOptions: {
        column: {
          groupPadding: 0,
          pointPadding: 0,
          stacking: 'normal'
        }
      },
      legend: {
        enabled: false
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          month: '%b %d',
          week: '%b %d',
          day: '%b %d'
        },
        title: {
          text: 'Date'
        }
        // min: this.dateRange[0].valueOf(),
        // max: this.dateRange[1].valueOf()
      },
      yAxis: {
        min: 0,
        title: {
          text: '# Fish Counted per Day'
        }
      },
      series: [
        {
          name: '# Fish',
          data: []
        }
      ]
    })
    this.charts.video = this.$highcharts.chart('chart-video', {
      chart: {
        type: 'column',
        height: 275,
        marginLeft: 75
      },
      title: {
        text: ''
      },
      plotOptions: {
        column: {
          groupPadding: 0,
          pointPadding: 0,
          stacking: 'normal'
        }
      },
      legend: {},
      tooltip: {
        shared: true
      },
      xAxis: {
        type: 'datetime',
        dateTimeLabelFormats: {
          month: '%b %d',
          week: '%b %d',
          day: '%b %d'
        },
        title: {
          text: 'Date'
        }
        // min: this.dateRange[0].valueOf(),
        // max: this.dateRange[1].valueOf()
      },
      yAxis: {
        min: 0,
        title: {
          text: '# Videos Recorded per Day'
        }
      },
      series: [
        {
          name: '# Counted',
          data: [],
          color: colors[3]
        },
        {
          name: '# Not Counted',
          data: []
        }
      ]
    })

    this.refresh()
    evt.$on('refresh', this.refresh)
  },
  beforeDestroy () {
    evt.$off('refresh', this.refresh)
  },
  methods: {
    refresh () {
      console.log('refresh')
      this.fetchStatusData().then(this.updateStatus)
      this.fetchRunData().then(this.updateRun)
    },
    fetchStatusData () {
      const utcFormat = d3TimeFormat.utcFormat('%b %d %I:%M %p EDT')
      const edtFormat = x => utcFormat(d3Time.timeHour.offset(x, -4))

      return this.$http
        .get('/status/', { params: { location_id: this.locationId } })
        .then(response => {
          const data = response.data.data

          const dataActivity = data.activity.map(d => ({
            x: new Date(d.count_timestamp).valueOf(),
            y: 0,
            z: d.count,
            duration: Math.round(d.duration),
            video_timestamp: edtFormat(new Date(d.video_start)),
            count_timestamp: edtFormat(new Date(d.count_timestamp))
          }))
          const dataFish = data.fish.map(d => [
            d3Time.timeHour.offset(new Date(d.date), 4).valueOf(),
            Math.round(d.count)
          ])
          const dataVideo = data.video.map(d => ({
            date: d3Time.timeHour.offset(new Date(d.date), 4),
            n_remaining: d.n_total - d.n_counted,
            n_counted: d.n_counted
          }))

          this.data.activity.total = dataActivity.length
          this.data.activity.values = dataActivity
          this.data.fish.total = dataFish.reduce((p, v) => p + v[1], 0)
          this.data.fish.values = dataFish
          this.data.video.total.counted = dataVideo.reduce(
            (p, v) => p + v.n_counted,
            0
          )
          this.data.video.total.remaining = dataVideo.reduce(
            (p, v) => p + v.n_remaining,
            0
          )
          this.data.video.values = dataVideo
        })
    },
    fetchRunData () {
      return this.$http
        .get('/run-estimate/', { params: { location_id: this.locationId } })
        .then(response => {
          const data = response.data.data

          this.data.run.values = data.map(d => {
            let tStar = jStat.studentt.inv(0.975, d.df)
            if (isNaN(tStar)) {
              tStar = 0
            }
            return {
              x: d3Time.timeHour.offset(new Date(d.date), 4).valueOf(),
              y: Math.round(d.y),
              low: Math.max(Math.round(d.y) - tStar * Math.sqrt(d.var_y), 0),
              high: Math.round(d.y) + tStar * Math.sqrt(d.var_y)
            }
          })

          const totalData = data.reduce(
            (p, v) => {
              p.y += v.y; // eslint-disable-line
              p.var_y += v.var_y; // eslint-disable-line
              p.df_num += v.df_num; // eslint-disable-line
              p.df_den += v.df_den; // eslint-disable-line
              return p
            },
            { y: 0, var_y: 0, df_num: 0, df_den: 0 }
          )

          totalData.df = Math.round(
            (totalData.df_num * totalData.df_num) / totalData.df_den
          )
          totalData.tStar = jStat.studentt.inv(0.975, totalData.df)
          // totalData.y = totalData.y
          totalData.ciRange = totalData.tStar * Math.sqrt(totalData.var_y)

          this.data.run.total = {
            y: Math.round(totalData.y),
            range: Math.round(totalData.ciRange)
          }
        })
    },
    updateStatus () {
      // activity
      this.odometers.activity.update(this.data.activity.total)
      this.charts.activity.series[0].setData(this.data.activity.values)

      // fish counts
      this.odometers.fish.update(Math.round(this.data.fish.total))
      this.charts.fish.series[0].setData(this.data.fish.values)

      // video counts
      this.odometers.video.counted.update(this.data.video.total.counted)
      this.odometers.video.remaining.update(this.data.video.total.remaining)
      this.charts.video.series[0].setData(this.data.video.values.map(d => ({
        x: d.date.valueOf(),
        y: d.n_counted
      })), false)
      this.charts.video.series[1].setData(this.data.video.values.map(d => ({
        x: d.date.valueOf(),
        y: d.n_remaining
      })))
    },
    updateRun () {
      this.odometers.run.total.update(this.data.run.total.y)
      this.odometers.run.range.update(this.data.run.total.range)
      this.charts.run.series[0].setData(this.data.run.values.map(d => ({
        x: d.x,
        y: d.y
      })), false)
      this.charts.run.series[1].setData(this.data.run.values.map(d => ({
        x: d.x,
        low: d.low,
        high: d.high
      })))
    }
  }
}
</script>

<style scoped>
.odometer-wrapper {
  font-weight: 600 !important;
  text-align: center !important;
}
.odometer-value {
  font-size: 2em !important;
}
.odometer-label {
  text-align: center !important;
  padding-top: 4px;
  color: #666 !important;
}
.small-text {
  margin-top: 4px;
  margin-bottom: 16px;
  font-size: 1em;
  line-height: 1.3;
  font-family: sans-serif;
  color: #888;
}
</style>
